// ABOUTME: Custom hook that encapsulates debounced server-side search state.
// Pair with SearchBar component and usePagination's handleParamsChange.
import { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';

const useSearch = (handleParamsChange, options = {}) => {
    const { debounceDelay = 400, getSearchParams = null } = options;

    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    // Ref so debounced fn always reads latest callbacks without being recreated
    const getSearchParamsRef = useRef(getSearchParams);
    useEffect(() => {
        getSearchParamsRef.current = getSearchParams;
    }, [getSearchParams]);

    const handleParamsChangeRef = useRef(handleParamsChange);
    useEffect(() => {
        handleParamsChangeRef.current = handleParamsChange;
    }, [handleParamsChange]);

    const debouncedSearchRef = useRef();

    useEffect(() => {
        debouncedSearchRef.current = debounce((query) => {
            const params = getSearchParamsRef.current
                ? getSearchParamsRef.current(query)
                : { search: query || undefined };
            handleParamsChangeRef.current?.(params);
        }, debounceDelay);

        return () => {
            debouncedSearchRef.current?.cancel();
        };
    }, [debounceDelay]);

    const handleSearchChange = useCallback((e) => {
        const val = e.target.value;
        setSearchQuery(val);
        debouncedSearchRef.current?.(val);
    }, []);

    const handleClearSearch = useCallback(() => {
        debouncedSearchRef.current?.cancel();
        setSearchQuery('');
        const params = getSearchParamsRef.current
            ? getSearchParamsRef.current('')
            : { search: undefined };
        handleParamsChangeRef.current?.(params);
        searchInputRef.current?.focus();
    }, []);

    // Resets query state only — caller handles the API call (e.g. clearFilters)
    const resetQuery = useCallback(() => {
        debouncedSearchRef.current?.cancel();
        setSearchQuery('');
    }, []);

    return {
        searchQuery,
        searchInputRef,
        handleSearchChange,
        handleClearSearch,
        resetQuery,
    };
};

export default useSearch;
